import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { taskSchema, type TaskFormData } from '@/lib/validators';
import { useTask, useTasks } from '@/composables/crm/useTasks';
import { useLeads } from '@/composables/crm/useLeads';
import { useContacts } from '@/composables/crm/useContacts';
import { useDeals } from '@/composables/crm/useDeals';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ArrowLeft } from 'lucide-react';
import { formatFullName } from '@/lib/formatters';

const statusOptions = [
  { label: 'To Do', value: 'TODO' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Cancelled', value: 'CANCELLED' },
];

const priorityOptions = [
  { label: 'Low', value: 'LOW' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'High', value: 'HIGH' },
  { label: 'Urgent', value: 'URGENT' },
];

export function TaskCreatePage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { createTask, updateTask } = useTasks();
  const { task, isLoading } = useTask(id || '');

  const { leads } = useLeads();
  const { contacts } = useContacts();
  const { deals } = useDeals();

  const leadOptions = [{ label: 'None', value: '_none' }, ...leads.map((l) => ({ label: formatFullName(l.firstName, l.lastName), value: l.id }))];
  const contactOptions = [{ label: 'None', value: '_none' }, ...contacts.map((c) => ({ label: formatFullName(c.firstName, c.lastName), value: c.id }))];
  const dealOptions = [{ label: 'None', value: '_none' }, ...deals.map((d) => ({ label: d.title, value: d.id }))];

  const form = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: { title: '', description: '', status: 'TODO', priority: 'MEDIUM', dueDate: '', assignedToId: '', leadId: '_none', contactId: '_none', dealId: '_none' },
  });

  useEffect(() => {
    if (task && isEdit) {
      form.reset({
        title: task.title || '',
        description: task.description || '',
        status: (task.status as TaskFormData['status']) || 'TODO',
        priority: (task.priority as TaskFormData['priority']) || 'MEDIUM',
        dueDate: task.dueDate || '',
        assignedToId: task.assignedToId || '',
        leadId: task.leadId || '_none',
        contactId: task.contactId || '_none',
        dealId: task.dealId || '_none',
      });
    }
  }, [task, isEdit, form]);

  const onSubmit = (data: TaskFormData) => {
    const cleaned = { ...data };
    if (cleaned.leadId === '_none') cleaned.leadId = '';
    if (cleaned.contactId === '_none') cleaned.contactId = '';
    if (cleaned.dealId === '_none') cleaned.dealId = '';

    if (isEdit) {
      updateTask.mutate({ id, data: cleaned }, { onSuccess: () => navigate(`/tasks/${id}`) });
    } else {
      createTask.mutate(cleaned, { onSuccess: () => navigate('/tasks') });
    }
  };

  if (isEdit && isLoading) return <LoadingSpinner />;

  const isPending = isEdit ? updateTask.isPending : createTask.isPending;

  return (
    <PageContainer backTo
      title={isEdit ? 'Edit Task' : 'Create Task'}
      description={isEdit ? 'Update task details' : 'Add a new task'}
      actions={
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      }
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Task Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <FormField form={form} name="title" label="Title" required />
              <FormTextarea form={form} name="description" label="Description" />
              <FormSelect form={form} name="status" label="Status" options={statusOptions} />
              <FormSelect form={form} name="priority" label="Priority" options={priorityOptions} />
              <FormDatePicker form={form} name="dueDate" label="Due Date" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Link to Entity</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <FormSelect form={form} name="leadId" label="Lead" options={leadOptions} />
              <FormSelect form={form} name="contactId" label="Contact" options={contactOptions} />
              <FormSelect form={form} name="dealId" label="Deal" options={dealOptions} />
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Task')}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
